/* eslint-disable class-methods-use-this */

/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2025 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of subscription software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 * */

/**
  * Assignment Fetcher
  * Common utility class to fetch assignments, MCQs, and status in a single aggregate API call
  *
  * @author Satyaki Bose
  */
import Framework from '@greenville/framework';
import { AET_STORE_EVENTS, ASSIGNMENT_GET_QUERY } from '../../modules/assignableEtext/common/Constants';
import EventRegister from '../utils/EventRegister';
import CommonUtils from '../utils/CommonUtils';

/**
 * Reusable utility to fetch assignments, MCQs, and status in a single aggregate call
 * Can be used in any container (Course, Product, Instructor Dashboard)
 */
export default class AssignmentFetcher {
  constructor(context, location) {
    this.context = context;
    this.eventBusId = `${location}_ASSIGNMENT_FETCHER`;
    this.setupEventListeners();
  }

  setupEventListeners() {
    EventRegister.on(AET_STORE_EVENTS.AGGREGATE_ASSIGNMENT_MCQ_COMPLETED, this.handleAggregateCompleted,
      this.eventBusId);
  }

  fetchAll() {
    this.fetchAggregate();
  }

  isEligibleForCourseAggregate = () => {
    const { course } = this.context.props;
    const { enableAET } = this.context.state.flags;
    const product = this.context.commonPlugin.getProduct();
    const isStudent = CommonUtils.getIsStudent({ courseUserRole: course?.role || product?.role });
    const isInstructor = !isStudent;

    return (isInstructor && enableAET && course?.isAet) || (isStudent && course?.isAet);
  };

  fetchAggregate = () => {
    const { course, user } = this.context.props;
    const { enableUnassignedMCQ, enableUnassignedAETForInstructor } = this.context.state.flags;
    const product = this.context.commonPlugin.getProduct();

    // LD flags check for instructor and student level AET enabled
    const eligibleForCourseAggregate = this.isEligibleForCourseAggregate();
    const eligibleForBookAggregate = enableUnassignedMCQ && product?.isAETEnabled;
    const isStudent = CommonUtils.getIsStudent({ courseUserRole: course?.role || product?.role });
    const isInstructor = !isStudent;

    if (eligibleForCourseAggregate) {
      const queryParams = (enableUnassignedAETForInstructor && CommonUtils.isRevelValidatedInstructor() && isInstructor)
        ? `&assignmentStatus=${ASSIGNMENT_GET_QUERY.INSTRUCTOR.assignmentStatus}` : '';
      Framework.getEventManager().publish(
        AET_STORE_EVENTS.AGGREGATE_ASSIGNMENT_MCQ_REQUESTED,
        {
          courseLevelIsAET: true,
          courseId: course?.id || null,
          bookId: product?.bookID || product?.id,
          userId: user.id,
          // clientVersion: 'V3', // TODO:we use V2 until validationAPI is present. Just enable this once ready.
          includeUnassigned: eligibleForBookAggregate,
          queryParams
        }
      );
      return;
    }
    if (eligibleForBookAggregate) {
      Framework.getEventManager().publish(
        AET_STORE_EVENTS.AGGREGATE_ASSIGNMENT_MCQ_REQUESTED,
        {
          courseLevelIsAET: false,
          courseId: null,
          bookId: product?.bookID || product?.id,
          userId: user.id
        }
      );
    }
  };

  /**
   * Handle aggregate API response completion
   * The service already combines the data - just handle course progress if needed
   */
  handleAggregateCompleted = (eventData) => {
    const { assignmentsData, mcqsData } = eventData || {};

    if (assignmentsData) {
      this.context.setState({ assignmentsData: assignmentsData || {} });
    }
    if (mcqsData) {
      this.context.setState({ mcqsData: mcqsData || {} });
    }
    this.context.setState({ isAggregateFetched: true });
  };

  cleanup() {
    EventRegister.cleanComponentEvents(this.eventBusId);
  }
}
